import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { reportsData } from '../data/reports';

const { 
  FiTrendingUp, FiClock, FiCheckCircle, FiAlertCircle, 
  FiDownload, FiEye, FiCalendar, FiActivity, FiList, FiChevronRight 
} = FiIcons;

const Dashboard = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('all');
  
  // Get unique assessments (latest version only for main list)
  const completedAssessments = Object.values(reportsData)
    .sort((a, b) => new Date(b.completedDate) - new Date(a.completedDate))
    .reduce((acc, current) => {
      const x = acc.find(item => item.assessmentTitle === current.assessmentTitle);
      if (!x) return acc.concat([current]);
      return acc;
    }, []);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const stats = [
    { title: 'Completed', value: Object.keys(reportsData).length, icon: FiCheckCircle, color: 'text-green-600 bg-green-100' },
    { title: 'Avg Score', value: '75%', icon: FiTrendingUp, color: 'text-blue-600 bg-blue-100' },
    { title: 'Open Tasks', value: '12', icon: FiList, color: 'text-orange-600 bg-orange-100' },
    { title: 'Reports', value: completedAssessments.length, icon: FiDownload, color: 'text-purple-600 bg-purple-100' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Assessment Dashboard</h1>
            <p className="text-gray-600">Track your business health and action items.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/compare" className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-all shadow-sm">
              <SafeIcon icon={FiActivity} className="mr-2" /> Compare
            </Link>
            <Link to="/recommendations" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-all shadow-md">
              <SafeIcon icon={FiList} className="mr-2" /> Action Items
            </Link>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => (
            <div key={stat.title} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <SafeIcon icon={stat.icon} className="w-5 h-5" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Completed List */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900">Active Assessments</h2>
                <Link to="/assessments" className="text-sm text-blue-600 font-bold hover:underline">Take New</Link>
              </div>
              <div className="divide-y divide-gray-100">
                {completedAssessments.map((assessment) => (
                  <div key={assessment.id} className="p-6 hover:bg-gray-50 transition-colors group">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">Latest: {assessment.completedDate}</span>
                        </div>
                        <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{assessment.assessmentTitle}</h3>
                      </div>
                      <div className={`text-lg font-bold ${getScoreColor(assessment.overallScore).split(' ')[0]}`}>
                        {assessment.overallScore}%
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Link 
                        to={`/report/${assessment.id}`} 
                        className="text-[11px] font-bold text-gray-600 bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200"
                      >
                        View Report
                      </Link>
                      <Link 
                        to={`/history/${encodeURIComponent(assessment.assessmentTitle)}`} 
                        className="text-[11px] font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 flex items-center"
                      >
                        View History <SafeIcon icon={FiChevronRight} className="ml-1" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Progress */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-6">Execution Status</h2>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between text-sm font-bold mb-2">
                    <span className="text-gray-600">Overall Progress</span>
                    <span className="text-blue-600">42%</span>
                  </div>
                  <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-blue-600 h-full rounded-full" style={{ width: '42%' }}></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                    <div className="text-xs font-bold text-green-600 uppercase mb-1">Completed</div>
                    <div className="text-xl font-bold text-green-900">8 Tasks</div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-xl border border-orange-100">
                    <div className="text-xs font-bold text-orange-600 uppercase mb-1">Next Priority</div>
                    <div className="text-sm font-bold text-orange-900">Document fulfillment SOPs</div>
                  </div>
                </div>
                <Link to="/recommendations" className="block text-center w-full py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-colors">
                  View All Action Items
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;